import { createSelector } from '@reduxjs/toolkit';

import { TDocStatus } from '@src-common/view/components/DocsList';

import { TGetSliceState, TSelectUploadedDocs } from './internal-types';
import { IDocUploaderForm } from './types';
import { createUploadForm } from './upload-form';

const createSelectors = <TDocType extends string>(
  getSliceState: TGetSliceState<TDocType>,
  getUploadedDocs: TSelectUploadedDocs<TDocType>,
) => {
  const getUploadDefinitions = (state: any) => getSliceState(state).docDefinitions;

  const getUploadDefinition = createSelector(
    (state: any) => getSliceState(state).uploadingForm.docType,
    getUploadDefinitions,

    (docType, docDefinitions) => docDefinitions.find(docDef => docDef.docType === docType),
  );

  const getUploadForm = (state: any) => getSliceState(state).uploadingForm;

  const isUploading = (state: any) => getSliceState(state).isPending;

  const getDocDefinitions = (state: any) => getSliceState(state).docDefinitions;

  const getCurrentDocType = (state: any): TDocType => getSliceState(state).uploadingForm.docType;

  const getValuesForUploadForm = createSelector(
    getCurrentDocType,
    getUploadedDocs,

    (docType, uploads): IDocUploaderForm<TDocType> | undefined => {
      const upload = uploads?.[docType];

      return upload === undefined ? undefined : createUploadForm(docType, upload);
    },
  );

  const getDocsList = createSelector(
    getDocDefinitions,
    getUploadedDocs,
    (docDefinitions, uploads) =>
      docDefinitions.map(docDefinition => ({
        ...docDefinition,
        status: (uploads[docDefinition.docType]?.status || 'missing') as TDocStatus,
        rejectedReason: uploads[docDefinition.docType]?.rejectReason,
        expiresAt: uploads[docDefinition.docType]?.expiresAt ?? null,
        expiresSoon: uploads[docDefinition.docType]?.expiresSoon ?? false,
      })),
  );

  return {
    getUploadDefinitions,
    getUploadDefinition,
    getUploadForm,
    isUploading,
    getDocDefinitions,
    getUploadedDocs,
    getCurrentDocType,
    getValuesForUploadForm,
    getDocsList,
  };
};

export { createSelectors };
